import { FC } from 'react'

import { Avatar, AvatarSizes } from '@inno-ui/avatar'
import {
  Button,
  ButtonColors,
  ButtonSizes,
  ButtonVariants
} from '@inno-ui/button'
import { useTranslation } from 'react-i18next'
import { ArtifactPositionShare, ArtifactShare } from 'shared/api/forgeApi'

import styles from './SharedList.module.scss'

interface SharedListProps {
  busy: boolean
  isOrgShared: boolean
  positionShares: ArtifactPositionShare[]
  userShares: ArtifactShare[]
  onRemovePosition: (positionId: string) => void
  onRemoveUser: (userId: string) => void
}

// "Shared with" section: empty state, or the list of position + people rows
export const SharedList: FC<SharedListProps> = ({
  busy,
  isOrgShared,
  positionShares,
  userShares,
  onRemovePosition,
  onRemoveUser
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'widgets.aiAgent' })

  return (
    <>
      <div className={styles.sectionLabel}>{t('sharedWithLabel')}</div>

      {userShares.length === 0 && positionShares.length === 0 ? (
        <p className={styles.emptyText}>
          {isOrgShared ? t('sharedWithOrgText') : t('notSharedYet')}
        </p>
      ) : (
        <ul className={styles.list}>
          {positionShares.map(p => (
            <li key={p.position_id} className={styles.personRow}>
              <span className={styles.personMeta}>
                <span className={styles.personName}>
                  {p.name || t('positionFallback', { id: p.position_id })}
                </span>
              </span>
              {/* position rows render no avatar, so a badge distinguishes
                  them from people rows at a glance */}
              <span className={styles.badge}>
                {t('sharedWithPositionsLabel')}
              </span>
              <Button
                label={t('remove')}
                size={ButtonSizes.S}
                variant={ButtonVariants.Ghost}
                color={ButtonColors.Red}
                disabled={busy}
                onClick={() => onRemovePosition(p.position_id)}
              />
            </li>
          ))}
          {userShares.map(s => (
            <li key={s.user_id} className={styles.personRow}>
              <Avatar url={s.avatar} username={s.name} size={AvatarSizes.L} />
              <span className={styles.personMeta}>
                <span className={styles.personName}>
                  {s.name || t('userFallback', { id: s.user_id })}
                </span>
                {s.email && <span className={styles.personSub}>{s.email}</span>}
              </span>
              <Button
                label={t('remove')}
                size={ButtonSizes.S}
                variant={ButtonVariants.Ghost}
                color={ButtonColors.Red}
                disabled={busy}
                onClick={() => onRemoveUser(s.user_id)}
              />
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
